import React, { useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { TextBox } from "@wattpad/web-ui-library";
import { postComment } from "@wattpad/client-platform-comments";

import { useTrans, useUserVerification } from "../../../hooks";
import { isReply } from "../CommentsUtils";
import { CommentsLocation } from "../CommentsConstants";
import { COMPONENT_NAME, PAGE_NAME } from "@wattpad/client-platform-web-events";

const PostNewComment = ({
  location,
  parentLocation,
  resource,
  isSticky,
  autofocus,
  placeholder,
  replyToUsername,
  onCommentPosted,
  sentimentEventData
}) => {
  const { trans } = useTrans();
  const { userNeedsVerification } = useUserVerification();

  const [hasError, setHasError] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [comment, setComment] = useState(
    replyToUsername && `@${replyToUsername} `
  );

  const placeholderText = placeholder || trans("Write a comment...");

  const handleCommentPosted = result => {
    result.isNewComment = true;
    setHasError(false);
    setIsPosting(false);
    setComment("");
    onCommentPosted(result);
  };

  const handleOnSendClick = () => {
    // TODO: tidy this up. It can be extracted to a helper function. Also check if buildPromptViewEvent
    // can work here in frontend-react.

    let component = undefined;

    // TODO: check if `isStoryPart` behaves here now, and add `isParagraph` if needed.
    if (location === CommentsLocation.STORY_PART) {
      component = COMPONENT_NAME.bottom_send_comment;
    }

    if (location === CommentsLocation.PARAGRAPH) {
      component = COMPONENT_NAME.right_send_comment;
    }

    if (
      parentLocation === CommentsLocation.STORY_PART &&
      isReply(resource.namespace)
    ) {
      component = COMPONENT_NAME.bottom_reply_send_comment;
    }

    if (
      parentLocation === CommentsLocation.PARAGRAPH &&
      isReply(resource.namespace)
    ) {
      component = COMPONENT_NAME.right_reply_send_comment;
    }

    const eventSource = {
      page: {
        [PAGE_NAME.reader]: {
          storyid: sentimentEventData?.storyid,
          partid: sentimentEventData?.partid
        }
      },
      component
    };

    if (userNeedsVerification({ eventSource })) return;
    setIsPosting(true);
    setHasError(false);
    postComment(resource, comment)
      .then(handleCommentPosted)
      .catch(() => {
        setHasError(true);
        setIsPosting(false);
      });
  };

  return (
    <div
      className={classNames("new-comment-field", {
        sticky: isSticky,
        replyTo: !!replyToUsername,
        reply: isReply(resource.namespace)
      })}
    >
      {replyToUsername && (
        <div className="reply-to-user">
          {trans("Replying to %s", replyToUsername)}
        </div>
      )}
      <TextBox
        text={comment}
        isLoading={isPosting}
        autoFocus={autofocus}
        onTextChange={setComment}
        placeholder={placeholderText}
        actionSend={handleOnSendClick}
        ariaLabel={trans("Post new comment")}
        errorMessage={hasError ? trans("Comment failed to post") : undefined}
      />
    </div>
  );
};

PostNewComment.propTypes = {
  location: PropTypes.string,
  parentLocation: PropTypes.string,
  resource: PropTypes.object.isRequired,
  isSticky: PropTypes.bool,
  autofocus: PropTypes.bool,
  placeholder: PropTypes.string,
  replyToUsername: PropTypes.string,
  onCommentPosted: PropTypes.func.isRequired,
  sentimentEventData: PropTypes.object
};

export default PostNewComment;
